/**
 * Public catalogue marketing page — ported from
 * `.scratch/bedd-snapshot/catalogue.html` into the bimassess SPA.
 *
 * NB: this is the *public marketing* catalogue, not the authenticated in-app
 * catalogue (`pages/catalogue/CataloguePage.tsx`). Both share the `/catalogue`
 * URL — `main.tsx` wraps them in `AuthSwitch`, so signed-out visitors land
 * here and signed-in members land on CataloguePage. `/catalogue.html` is a
 * stable alias that always renders this marketing variant (for external
 * references that pre-date the dual-mount setup). The export is renamed to
 * `CatalogueMarketingPage` so route-table imports stay unambiguous.
 *
 * Source script: sticky-nav + filter-strip compress, view toggle (Individual /
 * Enterprise card grids), filter-bar section filter, scroll reveals, final-
 * CTA spotlight.
 */

import { useEffect, useRef } from 'react';
import { markup } from './catalogue-assets/markup';
import './marketing-shared/marketing-shared.css';
import './catalogue-assets/catalogue.css';

export function CatalogueMarketingPage(): JSX.Element {
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    document.title = 'Catalogue · BIMAssess';
    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    // ─── STICKY NAV + FILTER-STRIP COMPRESS ──────
    const nav = wrapper.querySelector<HTMLElement>('nav.site-nav');
    const filterStrip = wrapper.querySelector<HTMLElement>('#filter-strip');
    const onScroll = (): void => {
      const scrolled = window.scrollY > 40;
      if (nav) nav.classList.toggle('scrolled', scrolled);
      if (filterStrip) filterStrip.classList.toggle('nav-compressed', scrolled);
    };
    window.addEventListener('scroll', onScroll, { passive: true });

    // ─── VIEW TOGGLE (Individual / Enterprise) ──
    const viewToggle = wrapper.querySelector<HTMLElement>('#view-toggle');
    const viewIndividual = wrapper.querySelector<HTMLElement>('#view-individual');
    const viewEnterprise = wrapper.querySelector<HTMLElement>('#view-enterprise');
    const viewBtns = viewToggle
      ? Array.from(viewToggle.querySelectorAll<HTMLButtonElement>('.view-btn'))
      : [];
    const viewHandlers: Array<{ btn: HTMLButtonElement; handler: () => void }> = [];
    viewBtns.forEach((btn) => {
      const handler = (): void => {
        viewBtns.forEach((b) => b.classList.remove('active'));
        btn.classList.add('active');
        const view = btn.dataset.view;
        if (viewIndividual)
          viewIndividual.style.display = view === 'individual' ? 'block' : 'none';
        if (viewEnterprise)
          viewEnterprise.style.display = view === 'enterprise' ? 'block' : 'none';
        if (filterStrip) filterStrip.style.display = view === 'individual' ? 'block' : 'none';
      };
      btn.addEventListener('click', handler);
      viewHandlers.push({ btn, handler });
    });

    // ─── FILTER BAR (section filter) ─────────────
    const filterBtns = Array.from(wrapper.querySelectorAll<HTMLButtonElement>('.filter-btn'));
    const filterHandlers: Array<{ btn: HTMLButtonElement; handler: () => void }> = [];
    filterBtns.forEach((btn) => {
      const handler = (): void => {
        filterBtns.forEach((b) => b.classList.remove('active'));
        btn.classList.add('active');
        const section = btn.dataset.section;
        wrapper.querySelectorAll<HTMLElement>('#view-individual .cat-section').forEach((s) => {
          if (section === 'all' || s.dataset.section === section) {
            s.style.display = 'block';
          } else {
            s.style.display = 'none';
          }
        });
      };
      btn.addEventListener('click', handler);
      filterHandlers.push({ btn, handler });
    });

    // ─── SCROLL REVEALS ──────────────────────────
    const revealObs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('in-view');
            revealObs.unobserve(e.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -5% 0px' },
    );
    wrapper.querySelectorAll('.reveal, .reveal-stagger').forEach((el) => revealObs.observe(el));

    // ─── FINAL CTA SPOTLIGHT ─────────────────────
    const ctaSection = wrapper.querySelector<HTMLElement>('.final-cta');
    const ctaMoveHandler = (e: MouseEvent): void => {
      if (!ctaSection) return;
      const rect = ctaSection.getBoundingClientRect();
      ctaSection.style.setProperty('--mx', `${e.clientX - rect.left}px`);
      ctaSection.style.setProperty('--my', `${e.clientY - rect.top}px`);
    };
    if (ctaSection) ctaSection.addEventListener('mousemove', ctaMoveHandler);

    // ─── REDUCED MOTION ──────────────────────────
    if (typeof window.matchMedia === 'function') {
      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
      if (reduced.matches) {
        wrapper.querySelectorAll('.reveal, .reveal-stagger').forEach((el) => {
          el.classList.add('in-view');
        });
      }
    }

    return () => {
      window.removeEventListener('scroll', onScroll);
      revealObs.disconnect();
      if (ctaSection) ctaSection.removeEventListener('mousemove', ctaMoveHandler);
      viewHandlers.forEach(({ btn, handler }) => btn.removeEventListener('click', handler));
      filterHandlers.forEach(({ btn, handler }) => btn.removeEventListener('click', handler));
    };
  }, []);

  return (
    <div
      ref={wrapperRef}
      className="bedd-marketing bedd-catalogue"
      dangerouslySetInnerHTML={{ __html: markup }}
    />
  );
}
